/**
 * Debug Utility
 *
 * Conditional logging that only outputs in development mode.
 * Prevents console noise in production while keeping debug info available during development.
 *
 * Usage:
 *   import { debug } from '@/lib/debug';
 *   debug.log('TurnkeyProvider', 'Session restored', { userId });
 *   debug.warn('WalletProvider', 'Connection unstable', { attempts });
 *   debug.error('RPC', 'Request failed', error);
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'log' | 'warn' | 'error' | 'info';

function createLogger(level: LogLevel) {
  return (context: string, message: string, data?: unknown) => {
    if (!isDev) return;

    const prefix = `[${context}]`;
    if (data !== undefined) {
      console[level](prefix, message, data);
    } else {
      console[level](prefix, message);
    }
  };
}

export const debug = {
  log: createLogger('log'),
  info: createLogger('info'),
  warn: createLogger('warn'),
  error: createLogger('error'),

  /**
   * Simple passthrough logging - works exactly like console.log but only in dev.
   * Use this for quick replacements of console.log calls.
   * @example debug.trace('Pool data:', poolData);
   */
  trace: (...args: unknown[]) => {
    if (!isDev) return;
    console.log(...args);
  },

  /** Group related logs together */
  group: (context: string, label: string, fn: () => void) => {
    if (!isDev) return;
    console.group(`[${context}] ${label}`);
    fn();
    console.groupEnd();
  },

  /** Log only if condition is true */
  when: (condition: boolean, context: string, message: string, data?: unknown) => {
    if (!isDev || !condition) return;
    const prefix = `[${context}]`;
    if (data !== undefined) {
      console.log(prefix, message, data);
    } else {
      console.log(prefix, message);
    }
  },

  /** Time an operation */
  time: (context: string, label: string) => {
    if (!isDev) return { end: () => {} };
    const key = `[${context}] ${label}`;
    console.time(key);
    return {
      end: () => console.timeEnd(key),
    };
  },
};
